import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { LogoMark } from "@/components/crm/Logo";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-zinc-950 text-white relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-red-600/20 blur-3xl" />
        <div className="absolute bottom-0 -left-24 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="flex items-center gap-2.5 relative">
          <LogoMark size={40} className="bg-white/10 ring-1 ring-white/15" />
          <div className="leading-none">
            <div className="font-heading font-extrabold text-lg tracking-tight">alternativeADV</div>
            <div className="text-[10px] text-zinc-400 overline mt-0.5">CRM FORMAZIONE ABILITANTE</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading text-5xl font-black tracking-tight leading-[1.05]">
            Nessun lead<br />deve essere<br /><span className="text-red-500">dimenticato.</span>
          </h1>
          <p className="mt-6 text-zinc-400 max-w-md">
            Il CRM commerciale che ogni mattina ti dice esattamente chi chiamare,
            chi ricontattare e qual è la prossima azione per ogni lead.
          </p>
        </div>
        <div className="relative text-xs text-zinc-500 overline">Ricorda · Organizza · Prioritizza · Registra</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5 animate-fade-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-6">
            <LogoMark size={40} />
            <div className="leading-none">
              <div className="font-heading font-extrabold text-lg">alternativeADV</div>
              <div className="text-[10px] text-muted-foreground overline mt-0.5">CRM FORMAZIONE ABILITANTE</div>
            </div>
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight">Accedi al CRM</h2>
            <p className="text-sm text-muted-foreground mt-1">Inserisci le tue credenziali per continuare.</p>
          </div>
          {error && <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">{error}</div>}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@alternativeadv.it" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
          </div>
          <Button data-testid="login-submit" type="submit" className="w-full" disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Accedi
          </Button>
          <div className="text-center">
            <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Password dimenticata?</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
